import type { Product } from '@mc/api'
import { trackGoldStandardPurchaseClick } from '@mc/pre-paywall-events'
import {
  track as mcTrack,
  EventTypes,
  Locations,
  getPageSource,
  type PrePaywallPageSource,
} from '@mc/track-event'
import { trackStepComplete } from 'pages/find-my-classes/components/Quiz/events'
import type {
  EventOptions,
  StepName,
} from 'pages/find-my-classes/components/Quiz/types'

export const trackCtaPurchaseClick = ({
  action,
  href,
  location = Locations.HERO,
  annualPass,
}: {
  action: 'Submit My Picks' | 'Continue'
  href: string
  location?: SharedOpts['location']
  annualPass?: Product | null
}) => {
  // Fires the gold-standard "Purchase Click".
  trackGoldStandardPurchaseClick({
    action,
    location,
    href,
    annualPass,
  })
}

// the class recs HP only has the primer question and the category question
// but also primer modified question
type Step = Extract<
  StepName,
  'primer-question' | 'primer-question-modified' | 'category-selection'
>

type SharedOpts = {
  step: 1 // always step 1 since these events are for tracking quiz on HP
  step_name: Step
  category: 'personalization-input'
  action: string
  location: typeof Locations.HERO | typeof Locations.QUIZ_MODULE
  page_source: PrePaywallPageSource
  page_path: string
}

type PersonalizationName = typeof EventTypes.PERSONALIZATION_STEP_CLICK
type PersonalizationOpts = SharedOpts & {
  checked?: boolean
  selected?: boolean
}

const trackPersonalization = (
  name: PersonalizationName,
  opts: PersonalizationOpts,
) => {
  mcTrack(name, opts)
}

// --- MOTIVATION VARIANTS ---

type PrimerStepName = {
  stepName: Extract<StepName, 'primer-question' | 'primer-question-modified'>
  location?: SharedOpts['location']
}

/**
 * Modified from pages/find-my-classes/components/Quiz/events.ts
 */
export const trackCheckboxToggled = ({
  value,
  checked,
  stepName,
  location = Locations.HERO,
}: Pick<EventOptions, 'value' | 'checked'> & PrimerStepName) => {
  trackPersonalization(EventTypes.PERSONALIZATION_STEP_CLICK, {
    step: 1, // tracking this on homepage makes it step 1
    step_name: stepName,
    category: 'personalization-input',
    location,
    action: value,
    checked,
    page_source: getPageSource(),
    page_path: window.location.pathname,
  })
}

type PrimerAnswerSubmittedArgs = {
  selectedOptions: EventOptions['selectedOptions']
  selectedCategories?: EventOptions['selectedCategories']
  selectedCategoryIds?: EventOptions['selectedCategoryIds']
} & PrimerStepName

export const trackPrimerAnswerSubmitted = ({
  selectedOptions,
  selectedCategories,
  selectedCategoryIds,
  stepName,
  location = Locations.HERO, // prev: 'body',
}: PrimerAnswerSubmittedArgs) => {
  trackStepComplete({
    step: 1, // since HP
    step_name: stepName,
    category: 'personalization-input',
    location,
    action: 'Continue', // prev: 'Next',
    selectedOptions,
    selectedCategories,
    selectedCategoryIds,
    page_source: getPageSource(),
    page_path: window.location.pathname,
  })
  // NOTE: The gold-standard "Purchase Click" is fired by
  // `trackCtaPurchaseClick` in the CTA (TooltipCta) itself. Do NOT fire it here
  // too, or it fires twice.
}
